import { useCallback } from 'react';

import { useToastService } from '@@common';

import { useClipboardPaste } from '../../../../common/hooks/useClipboardPaste';
import CopyToClipboardButton from '../../../../components/Clipboard/CopyToClipboardButton';
import PasteFromClipboardButton from '../../../../components/Clipboard/PasteFromClipboardButton';
import { ExpressionModel } from '../../Models/ExpressionModel';

interface Props {
	/** The current expression value. The copy button is disabled when unset. */
	expression: ExpressionModel | undefined;
	/** Called with the parsed expression on paste. When undefined the paste button is hidden. */
	onPaste?: (expression: ExpressionModel) => void;
	/** Also listen for keyboard Ctrl+V paste events (default: false). */
	keyboardPaste?: boolean;
}

/**
 * Renders a copy button and a paste button for an expression value.
 * Optionally enables keyboard paste via Ctrl+V.
 */
export function ExpressionCopyPasteActions({ expression, onPaste, keyboardPaste = false }: Props) {
	const toastService = useToastService();

	const applyPaste = useCallback(
		(text: string) => {
			try {
				const parsed = JSON.parse(text);
				if (!parsed?.Type) throw new Error('Not a valid expression');
				onPaste?.(parsed as ExpressionModel);
				toastService.success('Expression pasted');
			} catch {
				toastService.error('Clipboard does not contain a valid expression');
			}
		},
		[onPaste, toastService],
	);

	useClipboardPaste(applyPaste, !!onPaste && keyboardPaste);

	const handlePasteClick = useCallback(async () => {
		try {
			const text = await navigator.clipboard.readText();
			applyPaste(text);
		} catch {
			toastService.error('Failed to read clipboard');
		}
	}, [applyPaste, toastService]);

	return (
		<>
			<CopyToClipboardButton onClick={() => JSON.stringify(expression)} title="Copy expression to clipboard" disabled={expression === undefined} />
			{onPaste && <PasteFromClipboardButton onClick={handlePasteClick} title="Paste expression from clipboard" />}
		</>
	);
}
